import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast, Toaster } from "sonner";
import { CLINIC, SERVICES } from "@/data/content";
import { ArrowUpRight, Phone, MessageCircle, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookConsultation() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", preferred_date: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please share your name and phone number.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/appointments`, form);
      setDone(true);
      toast.success("Thank you — we\u2019ll call you back within a few hours.");
      setForm({ name: "", phone: "", email: "", service: "", preferred_date: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="book" className="py-20 md:py-32 soft-radial" data-testid="book-section">
      <Toaster position="top-center" richColors />
      <div className="max-w-[1440px] mx-auto px-5 md:px-12">
        <div className="grid grid-cols-12 gap-6 md:gap-12 items-start">
          {/* LEFT */}
          <div className="col-span-12 md:col-span-5">
            <div className="eyebrow mb-4">Book now</div>
            <h2 className="font-display text-3xl md:text-5xl lg:text-[3.4rem] leading-[1.05] tracking-[-0.02em]">
              Book your <br /><span className="text-[hsl(var(--primary))]">consultation.</span>
            </h2>
            <p className="mt-6 text-[hsl(var(--ink-soft))] max-w-md leading-relaxed">
              Share a few details and we&apos;ll call back within a few hours to confirm a slot at your convenience. Same-day appointments often available.
            </p>

            <div className="mt-8 space-y-3">
              {CLINIC.phones.map((p, i) => (
                <a key={p} href={`tel:${CLINIC.phoneDial[i]}`} className="card-soft p-5 flex items-center gap-4 group" data-testid={`book-phone-${i}`}>
                  <span className="w-11 h-11 rounded-full bg-[hsl(var(--primary))] text-white flex items-center justify-center"><Phone size={18} /></span>
                  <div>
                    <div className="text-[11px] text-[hsl(var(--ink-soft))] uppercase tracking-widest">Call now</div>
                    <div className="font-display text-lg leading-none mt-1">{p}</div>
                  </div>
                  <ArrowUpRight size={16} className="ml-auto text-[hsl(var(--primary-dark))] transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </a>
              ))}
              <a href={CLINIC.whatsapp} target="_blank" rel="noopener noreferrer" className="card-soft p-5 flex items-center gap-4 group" data-testid="book-whatsapp-cta">
                <span className="w-11 h-11 rounded-full bg-[#25D366] text-white flex items-center justify-center"><MessageCircle size={18} /></span>
                <div>
                  <div className="text-[11px] text-[hsl(var(--ink-soft))] uppercase tracking-widest">WhatsApp us</div>
                  <div className="font-display text-lg leading-none mt-1">Chat with the clinic</div>
                </div>
                <ArrowUpRight size={16} className="ml-auto text-[hsl(var(--primary-dark))]" />
              </a>
            </div>
          </div>

          {/* FORM */}
          <motion.form
            onSubmit={submit}
            className="col-span-12 md:col-span-7 card-soft p-5 md:p-10"
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
            data-testid="book-form"
          >
            {done ? (
              <div className="text-center py-14">
                <div className="w-16 h-16 mx-auto rounded-full bg-[hsl(var(--primary-light))] text-[hsl(var(--primary))] flex items-center justify-center mb-6"><Sparkles size={26}/></div>
                <div className="font-display text-3xl md:text-4xl leading-tight">Thank you.</div>
                <p className="mt-4 text-[hsl(var(--ink-soft))]">Your request is in — we&apos;ll be in touch shortly to confirm.</p>
                <button onClick={() => setDone(false)} className="btn-pill btn-outline mt-8" data-testid="book-form-reset">Book another <span className="arrow"><ArrowUpRight size={14}/></span></button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-5">
                <Field label="Your name" required col="col-span-2 md:col-span-1">
                  <input value={form.name} onChange={onChange("name")} className={inputCls} placeholder="John Doe" data-testid="book-input-name" />
                </Field>
                <Field label="Phone" required col="col-span-2 md:col-span-1">
                  <input value={form.phone} onChange={onChange("phone")} className={inputCls} placeholder="+91 ..." data-testid="book-input-phone" />
                </Field>
                <Field label="Email (optional)" col="col-span-2">
                  <input type="email" value={form.email} onChange={onChange("email")} className={inputCls} placeholder="you@example.com" data-testid="book-input-email" />
                </Field>
                <Field label="Interested in" col="col-span-2 md:col-span-1">
                  <select value={form.service} onChange={onChange("service")} className={inputCls} data-testid="book-input-service">
                    <option value="">Any / not sure</option>
                    {SERVICES.map((s) => (<option key={s.n} value={s.title}>{s.title}</option>))}
                  </select>
                </Field>
                <Field label="Preferred date" col="col-span-2 md:col-span-1">
                  <input type="date" value={form.preferred_date} onChange={onChange("preferred_date")} className={inputCls} data-testid="book-input-date" />
                </Field>
                <Field label="Anything we should know?" col="col-span-2">
                  <textarea rows={3} value={form.message} onChange={onChange("message")} className={inputCls + " resize-none"} data-testid="book-input-message" />
                </Field>
                <div className="col-span-2 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mt-2">
                  <span className="text-[12px] text-[hsl(var(--ink-soft))]">We&apos;ll respond within a few hours during clinic hours.</span>
                  <button type="submit" disabled={submitting} className="btn-pill btn-solid" data-testid="book-submit">
                    {submitting ? "Sending..." : "Send request"} <span className="arrow"><ArrowUpRight size={16}/></span>
                  </button>
                </div>
              </div>
            )}
          </motion.form>
        </div>
      </div>
    </section>
  );
}

const inputCls = "w-full rounded-xl bg-[hsl(var(--bg-2))]/50 border border-transparent focus:border-[hsl(var(--primary))] focus:bg-white outline-none px-4 py-3 text-[14px] text-[hsl(var(--ink))] transition-colors placeholder:text-[hsl(var(--ink-soft))]";

function Field({ label, required, col, children }) {
  return (
    <label className={`${col} flex flex-col gap-2`}>
      <span className="text-[12px] font-medium text-[hsl(var(--ink-soft))] uppercase tracking-widest">{label}{required && " *"}</span>
      {children}
    </label>
  );
}
